/* eslint-disable */
import { createSlice } from '@reduxjs/toolkit';
import { userActions } from '../actions';
import { authMethods } from 'appConstants';

const initialState = {
	authorizing: false,
	authenticated: false,
	isInternal: false,
	cloudEnvironments: [],
	authProviders: [],
	remoteAuths: [],
	authMethod: authMethods.LEGACY,
	isLoadingRemoteAuths: false,
	customerIds: [],
	inboxAccessList: [],
	canAddCandidate: true,
	bullhornIntegration: false,
	isSchedulingEnabled: false,
	hasLicense: true,
	isLoading: false,
	customerWhatsAppEnabled: false,
	smsDisabled: false,
	whatsAppEnabled: false,
	preferences: {
		isLoadingBlacklistedDomains: false,
		isLoadingPreferredPhoneRegions: false,
		blacklistedDomains: [],
		preferredPhoneRegions: [],
		isSwitchOn: true,
		showPreferences: false
	},
	// Ad-hoc for PeopleScout https://textrecruit.atlassian.net/browse/CE-150
	shouldBeAbleToAddCandidate: false,
	customer: {
		internalCustomerType: ''
	}
};

// Only allow USER & TEAM inboxes for now. -Amir
const theOnlyAllowedInboxTypesForNow = (inboxAccessList) => {
	const res = [];
	(inboxAccessList || []).forEach((inbox) => {
		if (['my inbox', 'team inbox'].includes(inbox.accountName.toLowerCase())) {
			res.push(inbox);
		}
	});
	return res;
};

const getSmsPhoneNumberAndWhatsAppFlag = (customerIds) => {
	const r = { smsPhoneNumber: null, whatsAppEnabled: false };
	(customerIds || []).forEach((customerId) => {
		if (customerId.product === 'TextRecruit') {
			if (customerId.smsPhoneNumber) {
				r.smsPhoneNumber = customerId.smsPhoneNumber;
				r.whatsAppEnabled = customerId.whatsAppEnabled;
			}
		}
	});
	return r;
};

const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {},
	extraReducers: {
		[userActions.toggleSwitch]: (state, action) => {
			if (action.payload === undefined) {
				state.preferences.isSwitchOn = !state.preferences.isSwitchOn;
			} else {
				state.preferences.isSwitchOn = action.payload;
			}
		},
		[userActions.setCurrentExtensionWebPageOrigin]: (state, action) => {
			state.currentExtensionWebPageOrigin = action.payload;
		},
		[userActions.showPreferences]: (state) => {
			state.preferences.showPreferences = !state.preferences.showPreferences;
		},
		[userActions.setAuthMethod]: (state, action) => {
			state.authMethod = action.payload;
		},
		[userActions.setIsInternal]: (state, action) => {
			const { username } = action.payload;
			state.isInternal = username.includes('@icims') || username.includes('@textrecruit');
		},
		[userActions.setTextRecruitDataFromExtension]: (state, action) => {
			state.textRecruitDataFromExtension = action.payload;
		},
		[userActions.lookup.fulfilled]: (state, action) => {
			const { providers = [] } = action.payload || {};
			const cloudEnvironments = [];
			for (const provider of providers) {
				if (provider.cloudEnvironment) {
					cloudEnvironments.push(provider.cloudEnvironment);
				}
			}
			state.cloudEnvironments = cloudEnvironments;
			state.authProviders = providers;
			state.auth0Provider = providers.find((auth) => auth.authType === 'AUTH0');
		},
		[userActions.sso.pending]: (state) => {
			state.isLoadingRemoteAuths = true;
		},
		[userActions.sso.fulfilled]: (state, action) => {
			const remoteAuths = action.payload.remoteAuths || [];
			state.isLoadingRemoteAuths = false;
			state.remoteAuths = remoteAuths;
			if (remoteAuths.length) {
				state.authMethod = authMethods.SSO;
			}
		},
		[userActions.sso.rejected]: (state) => {
			state.isLoadingRemoteAuths = false;
			state.remoteAuths = [];
		},
		[userActions.login.pending]: (state) => {
			state.authorizing = true;
			state.isLoading = true;
		},
		[userActions.login.fulfilled]: (state, action) => {
			state.authorizing = false;
			state.authenticated = true;
			state.isLoading = false;
			state.id = action.payload.id;
			state.firstName = action.payload.firstName;
			state.lastName = action.payload.lastName;
		},
		[userActions.login.rejected]: (state, action) => {
			state.authorizing = false;
			state.isLoading = false;
			state.error = action.payload.message;
		},
		[userActions.sessionInfo.fulfilled]: (state, action) => {
			const { blacklistedDomains, customerInfo, preferredPhoneRegions, userInfo } = action.payload;

			const { smsPhoneNumber, whatsAppEnabled } = getSmsPhoneNumberAndWhatsAppFlag(
				userInfo.customerIds
			);

			state.authenticated = true;
			state.id = userInfo.id;
			state.firstName = userInfo.firstName;
			state.lastName = userInfo.lastName;
			state.userName = userInfo.userName;
			state.email = userInfo.email;
			state.customerIds = userInfo.customerIds;
			state.hasLicense = Boolean(smsPhoneNumber);
			state.smsPhoneNumber = smsPhoneNumber;
			state.smsDisabled = Boolean(userInfo.smsDisabled);
			state.whatsAppEnabled = Boolean(whatsAppEnabled);
			state.facebookUserMaps = userInfo.facebookUserMaps;
			state.inboxAccessList = theOnlyAllowedInboxTypesForNow(userInfo.inboxAccessList);
			state.preferences.blacklistedDomains = blacklistedDomains;
			state.preferences.preferredPhoneRegions = preferredPhoneRegions || [];
			state.activeAccount = customerInfo.currentPlan;
			state.canAddCandidate = customerInfo.enableCandidateAdd;
			state.icimsUser = customerInfo.icimsUser;
			state.icimsIntegration = customerInfo.icimsIntegration;
			state.icimsCustomFieldCandidate = customerInfo.icimsCustomFieldCandidate;
			state.icimsCustomFieldJob = customerInfo.icimsCustomFieldJob;
			state.icimsCustomFieldResponseFlag = customerInfo.icimsCustomFieldResponseFlag;
			state.icimsCustomFieldWorkflow = customerInfo.icimsCustomFieldWorkflow;
			state.icimsPhonePrefix = customerInfo.icimsPhonePrefix;
			state.bullhornIntegration = customerInfo.bullhornIntegration;
			state.customerId = customerInfo.id;
			state.customerName = customerInfo.name;
			state.customerWhatsAppEnabled = Boolean(customerInfo.twilioAccountId);
			state.isSchedulingEnabled = customerInfo.enableCalendarLink;
			state.timeZone = userInfo.timeZone;
			state.customer.internalCustomerType = customerInfo.internalCustomerType;
		},
		[userActions.updateBlacklist.pending]: (state) => {
			state.preferences.isLoadingBlacklistedDomains = true;
		},
		[userActions.updateBlacklist.fulfilled]: (state, action) => {
			state.preferences.isLoadingBlacklistedDomains = false;
			state.preferences.blacklistedDomains = action.payload.blacklistedDomains;
		},
		[userActions.updateBlacklist.rejected]: (state) => {
			state.preferences.isLoadingBlacklistedDomains = false;
		},
		[userActions.updatePreferredPhoneRegions.pending]: (state) => {
			state.preferences.isLoadingPreferredPhoneRegions = true;
		},
		[userActions.updatePreferredPhoneRegions.fulfilled]: (state, action) => {
			state.preferences.isLoadingPreferredPhoneRegions = false;
			state.preferences.preferredPhoneRegions = action.payload.preferredPhoneRegions;
		},
		[userActions.updatePreferredPhoneRegions.rejected]: (state) => {
			state.preferences.isLoadingPreferredPhoneRegions = false;
		}
	}
});

export default userSlice;
